import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FiActivity, FiDollarSign, FiDroplet } from "react-icons/fi";
import { IoMdTrendingUp } from "react-icons/io";
import Header from "../component/Header";
import { API_BASE_URL } from "../utils/apiConfig";
import { FaRupeeSign } from "react-icons/fa";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import {
    BarChart,
    Bar
} from "recharts";
import {
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";

const COLORS = ["#22ff88", "#0ea5ff"];

const renderLabel = ({ name, percent }) => {
    return `${name}: ${(percent * 100).toFixed(0)}%`;
};

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#121212] border border-green-400 px-4 py-2 rounded-lg shadow-lg">
                <p className="text-green-400 font-semibold">
                    {payload[0].payload.name || payload[0].payload.date}
                </p>
                <p className="text-green-400 text-sm">
                    {payload[0].name} : {payload[0].value}
                </p>
            </div>
        );
    }
    return null;
};

function HomePage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("");

    const fetchStats = useCallback(async (deviceId) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const deviceParam = deviceId ? `?device_id=${deviceId}` : "";
            const response = await axios.get(`${API_BASE_URL}/api/dashboard/stats${deviceParam}`, config);
            setStats(response.data);
        } catch (err) {
            console.error("Failed to fetch dashboard stats", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                const token = localStorage.getItem("token");
                const config = { headers: { Authorization: `Bearer ${token}` } };
                const devicesRes = await axios.get(`${API_BASE_URL}/api/devices`, config);
                setDevices(devicesRes.data);
                // Auto-select first device for admin, all for superadmin
                const userRole = localStorage.getItem("role");
                let initialId = "";
                if (userRole !== "superadmin") {
                    initialId = devicesRes.data?.[0]?.device_id_PK || "";
                }
                setSelectedDeviceId(initialId);
                fetchStats(initialId);
            } catch (err) {
                console.error("Failed to init homepage", err);
                fetchStats("");
            }
        };
        init();
    }, [fetchStats]);

    const handleDeviceChange = (e) => {
        const id = e.target.value;
        setSelectedDeviceId(id);
        fetchStats(id);
    };

    if (loading) return <div className="bg-black min-h-screen text-white flex items-center justify-center">Loading Dashboard...</div>;
    if (!stats) return <div className="bg-black min-h-screen text-white flex items-center justify-center">Error loading data</div>;

    // Format sales data for chart
    const formattedSales = stats.salesChart.map(item => ({
        day: new Date(item.date).toLocaleDateString('en-GB', { weekday: 'short' }),
        sales: parseFloat(item.total)
    }));

    const selectedDevice = devices.find(d => String(d.device_id_PK) === String(selectedDeviceId));

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            {/* ── Device Selector (same style as InventoryPage) ── */}
            {devices.length > 0 && (
                <div className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-800 pb-6">
                    <div>
                        <h2 className="text-xl font-bold text-white">Dashboard Overview</h2>
                        <p className="text-gray-500 text-xs mt-1">
                            {selectedDevice
                                ? `Showing stats for: ${selectedDevice.device_name}`
                                : "Showing stats for all machines"}
                        </p>
                    </div>
                    <select
                        value={selectedDeviceId}
                        onChange={handleDeviceChange}
                        className="bg-[#1b1d1a] border border-gray-700 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 transition w-full md:w-auto"
                    >
                        {localStorage.getItem("role") === "superadmin" && <option value="">All Machines</option>}
                        {devices.map(device => (
                            <option key={device.device_id_PK} value={device.device_id_PK}>
                                {device.device_name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-[#ececec] text-sm">Machines Online</p>
                            <h2 className="text-green-400 text-3xl font-bold mt-2">
                                {stats.machinesOnline} / {stats.totalMachines}
                            </h2>
                            <p className="text-[#ececec] text-xs mt-1">
                                {stats.totalMachines > 0
                                    ? `${((stats.machinesOnline / stats.totalMachines) * 100).toFixed(0)}% uptime`
                                    : "0% uptime"}
                            </p>
                        </div>
                        <FiActivity className="text-green-800 text-4xl mt-3" />
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-[#ececec] text-sm">Today's Revenue</p>
                            <h2 className="text-green-400 text-3xl font-bold mt-2">₹{stats.revenueToday ?? 0}</h2>
                            <p className="text-green-500 text-xs mt-1">Total revenue today</p>
                        </div>
                        <FaRupeeSign className="text-green-700 text-4xl mt-3" />
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-[#ececec] text-sm">Shakes Dispensed</p>
                            <h2 className="text-blue-400 text-3xl font-bold mt-2">{stats.shakesDispensed}</h2>
                            <p className="text-[#ececec] text-xs mt-1">Last 24 hours</p>
                        </div>
                        <FiDroplet className="text-blue-900 text-4xl mt-3" />
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-[#ececec] text-sm">Weekly Growth</p>
                            <h2 className="text-yellow-400 text-3xl font-bold mt-2">{stats.weeklyGrowth}%</h2>
                            <p className="text-[#ececec] text-xs mt-1">vs last week</p>
                        </div>
                        <IoMdTrendingUp className="text-yellow-900 text-4xl mt-3" />
                    </div>
                </div>
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition h-96">
                    <h1 className="text-2xl font-bold text-green-400 mb-4">
                        Sales Over Last 7 Days
                    </h1>
                    <div className="w-full h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={formattedSales}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                                <XAxis dataKey="day" stroke="#ececec" />
                                <YAxis stroke="#ececec" />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "#1b1d1a",
                                        border: "1px solid #22c55e",
                                        color: "#22c55e",
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="sales"
                                    stroke="#22c55e"
                                    strokeWidth={3}
                                    dot={{ r: 5 }}
                                    activeDot={{ r: 8 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition h-96">
                    <h1 className="text-2xl font-bold text-green-400 mb-4">
                        Shakes by Flavour
                    </h1>
                    <div className="w-full h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats.flavorChart}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                                <XAxis dataKey="name" stroke="#ececec" />
                                <YAxis stroke="#ececec" />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar
                                    dataKey="count"
                                    radius={[8, 8, 0, 0]}
                                    fill="#22c55e"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition h-96">
                    <h1 className="text-2xl font-bold text-green-400 mb-4">
                        Base Usage Distribution
                    </h1>
                    <div className="w-full h-64 flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                <Pie
                                    data={stats.baseChart}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={80}
                                    dataKey="count"
                                    label={renderLabel}
                                    stroke="#121212"
                                    strokeWidth={2}
                                >
                                    {stats.baseChart.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend
                                    verticalAlign="bottom"
                                    iconType="square"
                                    wrapperStyle={{ color: "#22c55e" }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default HomePage;
